import React, { useState, useEffect, useRef } from 'react';

function AutocompleteInput({ 
  value, 
  onChange, 
  placeholder, 
  className = '',
  onSelectUser,
  usersWithGroups 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Obtener usuarios del archivo importado si existen
  const getImportedUsers = () => {
    return usersWithGroups || [];
  };

  // Combinar usuarios estáticos con importados y eliminar duplicados
  const getAllUsers = () => {
    const importedUsers = getImportedUsers();
    
    // Crear un mapa para eliminar duplicados basados en el nombre
    const userMap = new Map();
    
    // Agregar usuarios importados
    importedUsers.forEach(user => {
      if (user.name && user.name.trim() !== '') {
        // Si ya existe un usuario con el mismo nombre, combinar la información
        if (userMap.has(user.name)) {
          const existingUser = userMap.get(user.name);
          userMap.set(user.name, {
            name: user.name,
            group: user.group || existingUser.group || '',
            employeeNumber: user.employeeNumber || existingUser.employeeNumber || '',
            location: user.location || existingUser.location || '',
            lockerNumber: user.lockerNumber || existingUser.lockerNumber || ''
          });
        } else {
          userMap.set(user.name, {
            name: user.name,
            group: user.group || '',
            employeeNumber: user.employeeNumber || '',
            location: user.location || '',
            lockerNumber: user.lockerNumber || ''
          });
        }
      }
    });
    
    return Array.from(userMap.values());
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Función mejorada para filtrar usuarios por múltiples campos
  const filterUsers = (searchTerm) => {
    const allUsers = getAllUsers();
    
    if (!searchTerm) {
      // Si no hay término de búsqueda, devolver todos los usuarios ordenados
      return allUsers
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 10);
    }
    
    // Convertir término de búsqueda a minúsculas para comparación
    const term = searchTerm.toLowerCase().trim();
    
    // Filtrar usuarios que coincidan con cualquiera de los campos
    const filtered = allUsers.filter(usuario => {
      return (
        (usuario.name && usuario.name.toLowerCase().includes(term)) ||
        (usuario.employeeNumber && usuario.employeeNumber.toLowerCase().includes(term)) ||
        (usuario.location && usuario.location.toLowerCase().includes(term)) ||
        (usuario.lockerNumber && usuario.lockerNumber.toLowerCase().includes(term)) ||
        (usuario.group && usuario.group.toLowerCase().includes(term))
      );
    });
    
    // Ordenar alfabéticamente por nombre
    return filtered
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 10);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    console.log('AutocompleteInput - handleInputChange:', newValue);
    onChange(newValue);
    setHighlightedIndex(-1);
    
    const filtered = filterUsers(newValue);
    setFilteredUsers(filtered);
    setIsOpen(filtered.length > 0 || !!newValue);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : -1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          selectUser(filteredUsers[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const selectUser = (usuario) => {
    console.log('AutocompleteInput - Usuario seleccionado:', usuario);
    
    if (!usuario) return;
    
    // Primero llamar a onChange para actualizar el valor del input
    onChange(usuario.name);
    
    // Cerrar el dropdown
    setIsOpen(false);
    setHighlightedIndex(-1);
    setFilteredUsers([]);
    inputRef.current?.blur();
    
    // Llamar a la función de callback con el usuario completo
    if (onSelectUser) {
      console.log('AutocompleteInput - Llamando a onSelectUser con:', usuario);
      onSelectUser(usuario);
    }
  };

  const handleInputFocus = () => {
    const allUsers = getAllUsers()
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 10);
    
    setFilteredUsers(allUsers);
    setIsOpen(allUsers.length > 0);
  };

  const handleInputBlur = () => {
    // Pequeño retraso para permitir el click en las sugerencias
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  // Efecto para manejar el valor inicial
  useEffect(() => {
    if (value && !isOpen) {
      const allUsers = getAllUsers();
      const matchedUser = allUsers.find(user => 
        user.name.toLowerCase() === value.toLowerCase()
      );
      
      if (matchedUser && onSelectUser) {
        console.log('AutocompleteInput - Usuario encontrado por valor inicial:', matchedUser);
        onSelectUser(matchedUser);
      }
    }
  }, [value]);

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        className={`w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${className}`}
        placeholder={placeholder}
        autoComplete="off"
      />
      
      {isOpen && filteredUsers.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.map((usuario, index) => (
            <div
              key={`${usuario.name}-${usuario.group || ''}`}
              onClick={() => selectUser(usuario)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 transition-colors ${
                highlightedIndex === index 
                  ? 'bg-blue-50 text-blue-700' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="font-medium">
                {usuario.name}
              </div>
              <div className="text-xs text-gray-500 flex flex-wrap gap-1 mt-1">
                {usuario.employeeNumber && (
                  <span className="bg-gray-100 px-1 rounded">
                    Nº: {usuario.employeeNumber}
                  </span>
                )}
                {usuario.group && (
                  <span className="bg-gray-100 px-1 rounded">
                    Grupo: {usuario.group}
                  </span>
                )}
                {usuario.location && (
                  <span className="bg-gray-100 px-1 rounded">
                    Ubicación: {usuario.location}
                  </span>
                )}
                {usuario.lockerNumber && (
                  <span className="bg-gray-100 px-1 rounded">
                    Taquilla: {usuario.lockerNumber}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {isOpen && filteredUsers.length === 0 && value && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="px-3 py-2 text-sm text-gray-500">
            No se encontraron usuarios coincidentes
          </div>
        </div>
      )}
    </div>
  );
}

export default AutocompleteInput;